import React from 'react';

import FocusAreaCoreIcon from '@atlaskit/icon/core/focus-area';
import type { NewCoreIconProps } from '@atlaskit/icon/types';

import { getFocusAreaIconColor } from '../../common/utils';
import {
	FOCUS_AREA_STATUS_KEYS,
	type FocusAreaHealthKey,
	type FocusAreaIconStatus,
	type FocusAreaStatusKey,
} from '../../common/utils/types';

export type FocusAreaIconProps = {
	/**
	 * Focus area status key
	 * Legacy statuses use FocusAreaStatusKey. Configurable statuses must include their category so
	 * in-progress statuses can continue deriving appearance from health.
	 */
	status?: FocusAreaIconStatus | undefined;

	/**
	 * Focus area health key
	 * NOTE: DO NOT CHANGE, type safety is enforced to ensure proper values are passed to this prop
	 * If necessary, you can cast a string to FocusAreaHealthKey if you are sure it is one of the valid values
	 */
	health?: FocusAreaHealthKey | undefined;

	/**
	 * Overrides the status color with a custom color token
	 */
	customColor?: NewCoreIconProps['color'];

	/**
	 * Determines the spacing of the icon
	 * Size has been deprecated and replaced with spacing
	 * https://hello.atlassian.net/wiki/spaces/DST/pages/3748692796
	 */
	spacing?: NewCoreIconProps['spacing'];

	/**
	 * Test id for container element
	 */
	testId?: string;

	/**
	 * Accessible description for the focus area icon
	 */
	label?: string;
};

/**
 * @deprecated Use FocusAreaStatusKey and FocusAreaHealthKey seperately instead
 */
type FocusAreaDerivedStatusKey = FocusAreaStatusKey | FocusAreaHealthKey;

/**
 * @deprecated Use FocusAreaIcon instead, only supported for backwards compatibility
 */
export const LegacyFocusAreaIcon = ({
	status,
	health,
	customColor,
	spacing,
	testId,
	label,
}: Omit<FocusAreaIconProps, 'status'> & {
	status: FocusAreaDerivedStatusKey | undefined;
}): React.JSX.Element => {
	const color =
		status === 'on_track' || status === 'at_risk' || status === 'off_track'
			? getFocusAreaIconColor({
					customColor,
					status: FOCUS_AREA_STATUS_KEYS.IN_PROGRESS,
					health: status,
				})
			: getFocusAreaIconColor({ customColor, status, health });

	return (
		<FocusAreaCoreIcon
			color={color}
			spacing={spacing || 'none'}
			label={label || ''}
			testId={testId}
		/>
	);
};

export default function FocusAreaIcon({
	status,
	health,
	customColor,
	spacing,
	testId,
	label,
}: FocusAreaIconProps): React.JSX.Element {
	return (
		<FocusAreaCoreIcon
			color={getFocusAreaIconColor({ customColor, status, health })}
			spacing={spacing || 'none'}
			label={label || ''}
			testId={testId}
		/>
	);
}
